import { Close, Error, MoveDown } from "@mui/icons-material";
import {
  Alert,
  Box,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import axios from "axios";
import React, { useEffect, useState } from "react";
import LoadingButton from "../../../components/LoadingButton";
import { hostname, parseError } from "../../../utils";

interface MoveToListDialogProps {
  open: boolean;
  onClose: () => void;
  getToken: () => Promise<string>;
  eventListener: Function;
  companyId: string;
  listId: string;
  selectedRowIds: readonly string[];
  onSuccess: () => void;
}

interface List {
  id: string;
  name: string;
}

const MoveToListDialog: React.FC<MoveToListDialogProps> = ({
  open,
  onClose,
  getToken,
  eventListener,
  companyId,
  listId,
  selectedRowIds,
  onSuccess,
}) => {
  const [lists, setLists] = useState<List[]>([]);
  const [selectedListId, setSelectedListId] = useState("");
  const [removeFromList, setRemoveFromList] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingLists, setLoadingLists] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showResults, setShowResults] = useState(false);
  const [results, setResults] = useState<any>(null);

  useEffect(() => {
    if (open) {
      loadLists();
    }
  }, [open]);

  const loadLists = async () => {
    setLoadingLists(true);
    setError(null);

    try {
      const token = await getToken();
      const response = await axios.get(`${hostname}/lists`, {
        params: { companyId, minimal: true },
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data && response.data.items) {
        // Filter out the current list
        const otherLists = response.data.items.filter(
          (list: List) => list.id !== listId
        );
        setLists(otherLists);
      }
    } catch (err: any) {
      const errorMessage = parseError(err);
      setError(`Failed to load lists: ${errorMessage}`);
    } finally {
      setLoadingLists(false);
    }
  };

  const handleMove = async () => {
    setError(null);

    if (!selectedListId) {
      setError("Please select a list");
      return;
    }

    if (selectedRowIds.length === 0) {
      setError("No rows selected");
      return;
    }

    setLoading(true);

    try {
      const token = await getToken();
      const response = await axios.post(
        `${hostname}/lists/${listId}/move-to-list`,
        {
          companyId,
          rowIds: selectedRowIds,
          targetListId: selectedListId,
          removeFromList,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const { addedCount, failedCount, failedRows, removedFromList } =
        response.data;

      const selectedList = lists.find((l) => l.id === selectedListId);
      const listName = selectedList?.name || "list";

      // Store results and show results dialog
      setResults({
        addedCount,
        failedCount,
        failedRows: failedRows || [],
        removedFromList,
        listName,
      });
      setShowResults(true);
    } catch (err: any) {
      const errorMessage = parseError(err);
      setError(errorMessage);
      try {
        eventListener({
          type: "SET_ERROR",
          error: `Failed to move rows to list: ${errorMessage}`,
        });
      } catch (eventError) {
        console.error("EventListener error:", eventError);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setSelectedListId("");
      setRemoveFromList(false);
      setError(null);
      setShowResults(false);
      setResults(null);
      onClose();
    }
  };

  const handleResultsClose = () => {
    setShowResults(false);
    setResults(null);
    handleClose();
    onSuccess();
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown={loading}
      >
        <DialogTitle>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Box display="flex" alignItems="center" gap={1}>
              <MoveDown color="primary" />
              <Typography variant="h6">Move to List</Typography>
            </Box>
            <IconButton
              edge="end"
              color="inherit"
              onClick={handleClose}
              disabled={loading}
              size="small"
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Alert severity="info" sx={{ mb: 3 }}>
              Move {selectedRowIds.length} selected row
              {selectedRowIds.length !== 1 ? "s" : ""} to another list. Only
              columns with matching names will be copied.
            </Alert>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="list-select-label">Select List</InputLabel>
              <Select
                labelId="list-select-label"
                value={selectedListId}
                onChange={(e) => setSelectedListId(e.target.value)}
                label="Select List"
                disabled={loading || loadingLists}
              >
                {lists.length === 0 && !loadingLists && (
                  <MenuItem value="" disabled>
                    No other lists available
                  </MenuItem>
                )}
                {loadingLists && (
                  <MenuItem value="" disabled>
                    Loading lists...
                  </MenuItem>
                )}
                {lists.map((list) => (
                  <MenuItem key={list.id} value={list.id}>
                    {list.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControlLabel
              control={
                <Checkbox
                  checked={removeFromList}
                  onChange={(e) => setRemoveFromList(e.target.checked)}
                  disabled={loading}
                />
              }
              label="Remove from current list after moving"
            />

            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 2, display: "block" }}
            >
              Note: Only columns with matching names (case-insensitive) will be
              copied to the target list. Rows with no matching columns will be
              skipped.
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions>
          <LoadingButton
            onClick={handleClose}
            disabled={loading}
            variant="text"
          >
            Cancel
          </LoadingButton>
          <LoadingButton
            onClick={handleMove}
            loading={loading}
            variant="contained"
            color="primary"
            disabled={!selectedListId || loadingLists}
          >
            Move to List
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* Results Dialog */}
      <Dialog
        open={showResults}
        onClose={handleResultsClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography variant="h6">Move Results</Typography>
            <IconButton
              edge="end"
              color="inherit"
              onClick={handleResultsClose}
              size="small"
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {results && (
            <Box>
              {/* Success Summary */}
              {results.addedCount > 0 && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  Successfully added {results.addedCount} row
                  {results.addedCount !== 1 ? "s" : ""} to {results.listName}
                  {results.removedFromList && " and removed from current list"}
                </Alert>
              )}

              {/* Failure Summary */}
              {results.failedCount > 0 && (
                <>
                  <Alert severity="error" sx={{ mb: 2 }}>
                    Failed to add {results.failedCount} row
                    {results.failedCount !== 1 ? "s" : ""}
                  </Alert>

                  {/* Failed Rows Details */}
                  {results.failedRows.length > 0 && (
                    <Box>
                      <Typography
                        variant="subtitle2"
                        sx={{ mb: 1, fontWeight: 600 }}
                      >
                        Failed Rows:
                      </Typography>
                      <List dense>
                        {results.failedRows.map(
                          (failedRow: any, index: number) => (
                            <ListItem key={index} sx={{ pl: 0 }}>
                              <ListItemIcon sx={{ minWidth: 36 }}>
                                <Error color="error" fontSize="small" />
                              </ListItemIcon>
                              <ListItemText
                                primary={`Row ${index + 1}`}
                                secondary={failedRow.error}
                                secondaryTypographyProps={{
                                  sx: { fontSize: "0.875rem" },
                                }}
                              />
                            </ListItem>
                          )
                        )}
                      </List>
                    </Box>
                  )}
                </>
              )}

              {/* No rows processed */}
              {results.addedCount === 0 && results.failedCount === 0 && (
                <Alert severity="info">No rows were processed</Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <LoadingButton onClick={handleResultsClose} variant="contained">
            Close
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MoveToListDialog;
